import { queryClient, TableTitles } from "@/constants";
import { useDisclosure } from "@/hooks";
import {
	useDeleteUser,
	useGetUserList,
} from "@/hooks/query-hooks/useMeditators";
import { Button, Divider, Drawer, Input, Space } from "antd";
import { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { AiOutlineEye, AiOutlineLeft, AiOutlineSearch } from "react-icons/ai";
import { AnimatedComponent, DeleteConfirmationModal, PageTitle } from "../common";
import { RegistrantInformations } from "../Meditators";
import { RegistrantTable } from "../Meditators/RegistrantTable";

export function UserList() {
	const [keyword, setKeyword] = useState<string | null>(null);
	const { data, isLoading: isFirstLoading } = useGetUserList(1, 10);
	const totalElements = data?.totalElements;

	// fetching all user data to filter in Front-end, just like in Meditators Component
	const { data: allData, isLoading: isSecondLoading } = useGetUserList(
		1,
		totalElements,
		{
			enabled: !!totalElements,
			refetchOnMount: true,
		},
	);

	const users = allData
		? keyword !== null
			? allData?.content?.filter(
					({ user }) =>
						user?.name
							?.toLocaleLowerCase()
							.includes(keyword.toLocaleLowerCase()) ||
						user?.phone?.includes(keyword),
				)
			: allData?.content
		: [];

	const renderColumnKeys = [
		TableTitles.registrant_name,
		TableTitles.retreat_level,
		TableTitles.registrant_book_id,
		TableTitles.registrant_phone,
		TableTitles.registrant_nrc,
		TableTitles.registrant_address,
		TableTitles.action,
	];

	return (
		<AnimatedComponent>
			<Helmet>
				<title>အသုံးပြုသူများ</title>
			</Helmet>

			<PageTitle>
				User Accounts
			</PageTitle>
			<Divider />
			<Input
				value={keyword || ""}
				onChange={(e) => setKeyword(e.target.value)}
				onClear={() => setKeyword(null)}
				placeholder="Search users by Name / Phone"
				size="large"
				className="mb-4 !ml-auto !flex !w-80"
				prefix={
					<AiOutlineSearch
						className="cursor-text pointer-events-none text-gray-400"
						size={20}
					/>
				}
				allowClear
			/>

			<RegistrantTable
				dataSource={users}
				loading={isFirstLoading || isSecondLoading}
				renderColumnKeys={renderColumnKeys}
				TableActionComponent={TableActionComponent}
				pagination={{
					pageSize: 10,
				}}
			/>
		</AnimatedComponent>
	);
}

export const TableActionComponent = ({
	record,
}: {
	record: MeditatorWithMeditationRetreat;
}) => {
	const [opened, { toggle }] = useDisclosure(false);

	const { mutate, isMutating } = useDeleteUser();

	const handleDeleteUser = () => {
		mutate(record.user.id, {
			onSuccess: () => {
				queryClient.invalidateQueries({
					queryKey: ["meditators"],
				});
			},
		});
	};

	return (
		<Space>
			<Button type="link" onClick={toggle}>
				<AiOutlineEye size={16} />
			</Button>
			<Drawer
				open={opened}
				onClose={toggle}
				closeIcon={false}
				destroyOnClose
				width={"79vw"}
				classNames={{
					content: "!w-[cal(100% - 320px)]",
				}}
				title={
					<div className="flex items-center justify-between">
						<Button
							shape="round"
							onClick={toggle}
							type="text"
							size="large"
							icon={<AiOutlineLeft />}
							className="!font-poppins"
						>
							Back
						</Button>

						<DeleteConfirmationModal
							isDeleting={isMutating}
							onDelete={handleDeleteUser}
							buttonProps={{
								type: "primary",
								danger: true,
								size: "large",
								className: "!font-poppins",
							}}
							description="Are you sure you want to delete this user?"
							title="Delete User"
						/>
					</div>
				}
			>
				<RegistrantInformations registrant={record.user} />
			</Drawer>
		</Space>
	);
};
